import {
  MapMarker as HereMapMarker,
  useHereMap,
} from '@backoffice-frontend/here-maps'
import type { ReactNode } from 'react'
import { useRef, useState } from 'react'
import {
  getMapLevel,
  getMapMarkerLevelPlacement,
} from '../HereMap/utils/mapMarkerLevel'

type MapMarkerProps = {
  children: ReactNode
  handleMarkerClick?: () => void
  id: string
  isActive?: boolean
  animateLocationChanges?: boolean
  isElevated?: boolean
  lat: number
  lng: number
}

/**
 * Add a marker to the map. This marker uses the here-maps MapMarker component
 * to render its children directly on the map.
 * @param {children} children - The children to render
 * @param {handleMarkerClick} handleMarkerClick - The function to call when the marker is clicked
 * @param {id} id - The id of the marker
 * @param {isActive} isActive - Whether this marker is currently selected/active (affects z-index)
 * @param {animateLocationChanges} animateLocationChanges - Whether to animate marker movement on location changes (defaults to false)
 * @param {isElevated} isElevated - Whether this marker should be elevated above default markers (e.g. has alerts)
 * @param {lat} lat - The latitude of the marker
 * @param {lng} lng - The longitude of the marker
 */
export const MapMarker = ({
  children,
  handleMarkerClick,
  id,
  isActive,
  animateLocationChanges,
  isElevated,
  lat,
  lng,
}: MapMarkerProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const { getCenter } = useHereMap()
  const handleMarkerClickRef = useRef(handleMarkerClick)
  handleMarkerClickRef.current = handleMarkerClick

  const mapCenterOnMouseDownRef = useRef<
    { latitude: number; longitude: number } | undefined
  >(undefined)

  const mapLevel = getMapLevel(isHovered, isActive, isElevated)
  const zIndex = getMapMarkerLevelPlacement(mapLevel)

  const onMouseDown = () => {
    mapCenterOnMouseDownRef.current = getCenter()
  }

  const onClick = () => {
    const threshold = 0.000000000000001
    const mapCenter = getCenter()
    const saved = mapCenterOnMouseDownRef.current
    const moved =
      Math.abs((mapCenter?.latitude ?? 0) - (saved?.latitude ?? 0)) >
        threshold ||
      Math.abs((mapCenter?.longitude ?? 0) - (saved?.longitude ?? 0)) >
        threshold
    if (!moved) {
      handleMarkerClickRef.current?.()
    }
  }

  return (
    <HereMapMarker
      position={{ latitude: lat, longitude: lng }}
      zIndex={zIndex}
      animatePosition={animateLocationChanges}
    >
      <div
        id={id}
        onMouseOver={() => setIsHovered(true)}
        onFocus={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onBlur={() => setIsHovered(false)}
        onMouseDown={onMouseDown}
        onClick={onClick}
      >
        {children}
      </div>
    </HereMapMarker>
  )
}
